import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Plus,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    FileText,
    Calendar,
    MapPin,
    Briefcase,
    X,
    Info,
    Loader,
} from "lucide-react";
import Swal from "sweetalert2";
import {
    getMyMissionRequestsRequest,
    createMissionRequestRequest,
    cancelMissionRequestRequest,
} from "../../../../redux/actions/missionRequestActions";
import { fetchOrdreMissionActifsRequest } from "../../../../redux/actions/ordreMissionActions";
import "../MesrequetesStyles/MesOrdres.css";

const MesOrdres = () => {
    const dispatch = useDispatch();

    // Redux state
    const { myRequests, loading } = useSelector((state) => state.missionRequests || {
        myRequests: [],
        loading: false,
    });
    const { ordresMissionActifs } = useSelector((state) => state.ordreMission || { ordresMissionActifs: [] });
    const user = useSelector((state) => state.auth.user);

    const [activeTab, setActiveTab] = useState("nouvelle");
    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);

    const [formData, setFormData] = useState({
        ordreMissionId: "",
        missionNom: "",
        motif: "",
        urgence: false,
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(fetchOrdreMissionActifsRequest());
        dispatch(getMyMissionRequestsRequest());
    }, [dispatch]);

    const getCouleurByWorkflow = (workflow) => {
        const colors = {
            manager: "#3b82f6",
            admin: "#8b5cf6",
            both: "#10b981",
        };
        return colors[workflow] || "#6b7280";
    };

    // Calculer les stats du collaborateur
    const collaborateurStats = useMemo(() => {
        if (!user) return {
            demandesEnCours: 0,
            demandesApprouvees: 0,
            demandesRejetees: 0,
            totalDemandes: 0,
        };

        const demandesEnCours = myRequests.filter(
            req => req.status === "PENDING_MANAGER" || req.status === "PENDING_ADMIN"
        ).length;

        const demandesApprouvees = myRequests.filter(
            req => req.status === "APPROVED"
        ).length;

        const demandesRejetees = myRequests.filter(
            req => req.status === "REJECTED"
        ).length;

        return {
            demandesEnCours,
            demandesApprouvees,
            demandesRejetees,
            totalDemandes: myRequests.length,
        };
    }, [user, myRequests]);

    // Transformer les ordres de mission actifs pour l'affichage
    const missionsDisponibles = useMemo(() => {
        if (!ordresMissionActifs || ordresMissionActifs.length === 0) return [];

        return ordresMissionActifs.map(om => ({
            id: om.id,
            code: om.code,
            libelle: om.libelle,
            description: om.description,
            dateDebut: om.dateDebut,
            dateFin: om.dateFin,
            lieu: om.lieu,
            centreCout: om.centreCout,
            workflow: om.workflow,
            couleur: getCouleurByWorkflow(om.workflow),
        }));
    }, [ordresMissionActifs]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const getWorkflowLabel = (workflow) => {
        const labels = {
            manager: "Validation Manager",
            admin: "Validation RH",
            both: "Manager + RH",
        };
        return labels[workflow] || workflow;
    };

    const resetForm = () => {
        setFormData({
            ordreMissionId: "",
            missionNom: "",
            motif: "",
            urgence: false,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.ordreMissionId) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: "Veuillez sélectionner une mission",
                confirmButtonColor: "#204385",
            });
            return;
        }

        const payload = {
            ordreMissionId: Number(formData.ordreMissionId),
            motif: formData.motif || null,
            urgent: formData.urgence,
        };

        dispatch(createMissionRequestRequest(payload));
        setShowModal(false);
        resetForm();

        setTimeout(() => {
            setActiveTab("historique");
        }, 1000);
    };

    const handleCancel = (id) => {
        Swal.fire({
            title: "Confirmation",
            text: "Voulez-vous vraiment annuler cette demande ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, annuler",
            cancelButtonText: "Non",
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(cancelMissionRequestRequest(id));
            }
        });
    };

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "En attente Manager", cls: "maBadge maBadge--warn", Icon: Clock },
            PENDING_ADMIN: { label: "En attente RH", cls: "maBadge maBadge--warn", Icon: Clock },
            APPROVED: { label: "Approuvé", cls: "maBadge maBadge--ok", Icon: CheckCircle },
            REJECTED: { label: "Rejeté", cls: "maBadge maBadge--no", Icon: XCircle },
            CANCELLED: { label: "Annulé", cls: "maBadge", Icon: XCircle },
        };

        const c = config[statut] || { label: statut, cls: "maBadge", Icon: AlertCircle };
        const I = c.Icon;
        return (
            <span className={c.cls}>
                <I size={14} />
                {c.label}
            </span>
        );
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const openModal = (mission) => {
        setFormData({
            ordreMissionId: mission.id,
            missionNom: mission.libelle,
            motif: "",
            urgence: false,
        });
        setShowModal(true);
    };

    if (loading && myRequests.length === 0 && missionsDisponibles.length === 0) {
        return (
            <div className="maPage">
                <div className="maWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="maPage">
            <div className="maWrap">
                {/* Header */}
                <div className="maHeader">
                    <h1>Mes demandes de mission</h1>
                    <p>Postulez aux missions disponibles et suivez vos demandes</p>
                </div>

                {/* Stats */}
                <div className="maStats">
                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--warn">
                                <Clock size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">Demandes en cours</div>
                                <div className="maStatValue maStatValue--pri">
                                    {collaborateurStats.demandesEnCours}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--ok">
                                <CheckCircle size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">Approuvées</div>
                                <div className="maStatValue maStatValue--pri">
                                    {collaborateurStats.demandesApprouvees}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--no">
                                <XCircle size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">Rejetées</div>
                                <div className="maStatValue maStatValue--pri">
                                    {collaborateurStats.demandesRejetees}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="maStatCard">
                        <div className="maStatLeft">
                            <div className="maStatIcon maStatIcon--info">
                                <Briefcase size={22} />
                            </div>
                            <div>
                                <div className="maStatLabel">Total demandes</div>
                                <div className="maStatValue maStatValue--pri">
                                    {collaborateurStats.totalDemandes}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card + Tabs */}
                <div className="maCard maCard--noPad">
                    <div className="maTabs">
                        <button
                            className={`maTab ${activeTab === "nouvelle" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("nouvelle")}
                        >
                            <Plus size={18} />
                            Nouvelle demande
                        </button>

                        <button
                            className={`maTab ${activeTab === "historique" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("historique")}
                        >
                            <FileText size={18} />
                            Historique
                            <span className={`maCount ${activeTab === "historique" ? "is-active" : ""}`}>
                                {myRequests.length}
                            </span>
                        </button>
                    </div>

                    <div className="maCardBody">
                        {/* Nouvelle demande */}
                        {activeTab === "nouvelle" && (
                            <>
                                <div className="maSectionHead">
                                    <div>
                                        <div className="maSectionTitle">Missions disponibles</div>
                                        <div className="maSectionSub">
                                            Sélectionnez la mission à laquelle vous souhaitez participer
                                        </div>
                                    </div>
                                </div>

                                {loading ? (
                                    <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                                        <Loader size={32} className="spin" />
                                    </div>
                                ) : missionsDisponibles.length === 0 ? (
                                    <div className="maEmpty">
                                        <AlertCircle size={38} />
                                        <p>Aucune mission disponible pour le moment</p>
                                    </div>
                                ) : (
                                    <div className="maGrid2">
                                        {missionsDisponibles.map((mission) => (
                                            <div
                                                key={mission.id}
                                                className="maTypeCard is-eligible"
                                                style={{ borderColor: mission.couleur }}
                                                onClick={() => openModal(mission)}
                                                role="button"
                                                tabIndex={0}
                                            >
                                                <div className="maTypeTop">
                                                    <div className="maTypeLeft">
                                                        <div
                                                            className="maTypeIcon"
                                                            style={{
                                                                backgroundColor: `${mission.couleur}20`,
                                                                color: mission.couleur
                                                            }}
                                                        >
                                                            <Briefcase size={18} />
                                                        </div>
                                                        <div>
                                                            <div className="maTypeName">{mission.libelle}</div>
                                                            <div
                                                                className="maPill"
                                                                style={{
                                                                    backgroundColor: `${mission.couleur}20`,
                                                                    color: mission.couleur
                                                                }}
                                                            >
                                                                {mission.code}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="maTypeDesc">{mission.description || "Aucune description"}</div>

                                                <div className="maTypeGrid">
                                                    <div>
                                                        <div className="maMuted">Date début</div>
                                                        <div className="maStrong" style={{ color: mission.couleur }}>
                                                            {formatDate(mission.dateDebut)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Date fin</div>
                                                        <div className="maStrong">
                                                            {formatDate(mission.dateFin)}
                                                        </div>
                                                    </div>
                                                    {mission.lieu && (
                                                        <div>
                                                            <div className="maMuted">Lieu</div>
                                                            <div className="maStrong">
                                                                <MapPin size={14} style={{ display: "inline", marginRight: 4 }} />
                                                                {mission.lieu}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="maMuted">Validation</div>
                                                        <div className="maStrong">
                                                            {getWorkflowLabel(mission.workflow)}
                                                        </div>
                                                    </div>
                                                </div>

                                                {mission.centreCout && (
                                                    <div style={{ marginTop: 8, fontSize: 13, color: "#6b7280" }}>
                                                        Centre de coûts: {mission.centreCout}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="maInfoBox">
                                    <Info size={18} />
                                    <div>
                                        <div className="maInfoTitle">Informations importantes</div>
                                        <ul className="maInfoList">
                                            <li>Les demandes sont soumises à validation selon le workflow configuré</li>
                                            <li>Vous serez notifié de l'avancement de votre demande</li>
                                            <li>Seules les missions actives peuvent faire l'objet d'une demande</li>
                                            <li>Vous pouvez annuler votre demande tant qu'elle n'est pas approuvée</li>
                                        </ul>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Historique */}
                        {activeTab === "historique" && (
                            <>
                                {loading ? (
                                    <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                                        <Loader size={32} className="spin" />
                                    </div>
                                ) : myRequests.length === 0 ? (
                                    <div className="maEmpty">
                                        <FileText size={38} />
                                        <p>Aucune demande enregistrée</p>
                                        <button
                                            className="maBtn maBtn--accent"
                                            onClick={() => setActiveTab("nouvelle")}
                                        >
                                            <Plus size={18} />
                                            Faire une demande
                                        </button>
                                    </div>
                                ) : (
                                    <div className="maHistory">
                                        {myRequests.map((d) => (
                                            <button
                                                key={d.id}
                                                className={`maHistoryCard ${d.urgent ? "is-urgent" : ""}`}
                                                onClick={() => showDetails(d)}
                                            >
                                                <div className="maHistoryTop">
                                                    <div className="maHistoryLeft">
                                                        <div
                                                            className="maHistoryIcon"
                                                            style={{
                                                                backgroundColor: `${getCouleurByWorkflow(d.ordreMissionWorkflow)}20`,
                                                                color: getCouleurByWorkflow(d.ordreMissionWorkflow)
                                                            }}
                                                        >
                                                            <Briefcase size={18} />
                                                        </div>

                                                        <div>
                                                            <div className="maHistoryTitle">
                                                                {d.ordreMissionLibelle} {d.urgent && <span className="maFire">🔥</span>}
                                                            </div>
                                                            <div className="maSub">
                                                                Demandé le {formatDate(d.createdAt)}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {getStatutBadge(d.status)}
                                                </div>

                                                <div className="maTypeGrid">
                                                    <div>
                                                        <div className="maMuted">Code</div>
                                                        <div className="maStrong">{d.ordreMissionCode}</div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Date début</div>
                                                        <div className="maStrong">
                                                            {formatDate(d.ordreMissionDateDebut)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Date fin</div>
                                                        <div className="maStrong">
                                                            {formatDate(d.ordreMissionDateFin)}
                                                        </div>
                                                    </div>
                                                    {d.ordreMissionLieu && (
                                                        <div>
                                                            <div className="maMuted">Lieu</div>
                                                            <div className="maStrong">
                                                                <MapPin size={14} style={{ display: "inline", marginRight: 4 }} />
                                                                {d.ordreMissionLieu}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {d.status === "REJECTED" && (d.adminComment || d.managerComment) && (
                                                    <div className="maReject">
                                                        <div className="maMuted">Motif du rejet</div>
                                                        <div className="maRejectText">
                                                            {d.adminComment || d.managerComment}
                                                        </div>
                                                    </div>
                                                )}

                                                {(d.status === "PENDING_MANAGER" || d.status === "PENDING_ADMIN") && (
                                                    <div style={{ marginTop: 12 }}>
                                                        <button
                                                            className="maBtn maBtn--sm maBtn--outline maBtn--danger"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCancel(d.id);
                                                            }}
                                                        >
                                                            <X size={14} />
                                                            Annuler la demande
                                                        </button>
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL - Demande */}
            {showModal && (
                <div className="maModal">
                    <div className="maModal__panel">
                        <div className="maModal__header">
                            <div className="maModal__headLeft">
                                <div className="maModal__icon">
                                    <Briefcase size={18} />
                                </div>
                                <div>
                                    <div className="maModal__title">Nouvelle demande de mission</div>
                                    <div className="maModal__sub">{formData.missionNom}</div>
                                </div>
                            </div>

                            <button
                                className="maIconBtn"
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                                aria-label="Fermer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="maModal__body">
                            <form onSubmit={handleSubmit}>
                                {formData.ordreMissionId && (
                                    <div className="maInfoInline">
                                        {(() => {
                                            const mission = missionsDisponibles.find((m) => m.id === Number(formData.ordreMissionId));
                                            if (!mission) return null;
                                            return (
                                                <div className="maInlineGrid">
                                                    <div>
                                                        <div className="maMuted">Code</div>
                                                        <div className="maStrong" style={{ color: mission.couleur }}>
                                                            {mission.code}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Date début</div>
                                                        <div className="maStrong">{formatDate(mission.dateDebut)}</div>
                                                    </div>
                                                    <div>
                                                        <div className="maMuted">Date fin</div>
                                                        <div className="maStrong">{formatDate(mission.dateFin)}</div>
                                                    </div>
                                                    {mission.lieu && (
                                                        <div>
                                                            <div className="maMuted">Lieu</div>
                                                            <div className="maStrong">
                                                                <MapPin size={14} style={{ display: "inline", marginRight: 4 }} />
                                                                {mission.lieu}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                <div className="maField">
                                    <label>Motif de la demande (optionnel)</label>
                                    <textarea
                                        rows={4}
                                        value={formData.motif}
                                        onChange={(e) => setFormData((p) => ({ ...p, motif: e.target.value }))}
                                        placeholder="Expliquez pourquoi vous souhaitez participer à cette mission..."
                                    />
                                </div>

                                <div className="maCheckRow">
                                    <label className="maCheck">
                                        <input
                                            type="checkbox"
                                            checked={formData.urgence}
                                            onChange={(e) => setFormData((p) => ({ ...p, urgence: e.target.checked }))}
                                        />
                                        <span>Marquer comme urgent</span>
                                    </label>
                                    <span className="maHelp">Les demandes urgentes sont traitées en priorité</span>
                                </div>

                                <div className="maModal__footer">
                                    <button
                                        type="button"
                                        className="maBtn maBtn--ghost"
                                        onClick={() => {
                                            setShowModal(false);
                                            resetForm();
                                        }}
                                    >
                                        Annuler
                                    </button>
                                    <button type="submit" className="maBtn maBtn--accent" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <Loader size={16} className="spin" />
                                                Envoi en cours...
                                            </>
                                        ) : (
                                            "Soumettre"
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL - Détails */}
            {showDetailsModal && selectedDemande && (
                <div className="maModal">
                    <div className="maModal__panel">
                        <div className="maModal__header">
                            <div className="maModal__headLeft">
                                <div className="maModal__icon">
                                    <FileText size={18} />
                                </div>
                                <div>
                                    <div className="maModal__title">Détails de la demande #{selectedDemande.id}</div>
                                    <div className="maModal__sub">{selectedDemande.ordreMissionLibelle}</div>
                                </div>
                            </div>

                            <button className="maIconBtn" onClick={() => setShowDetailsModal(false)} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="maModal__body">
                            <div style={{ marginBottom: 16 }}>{getStatutBadge(selectedDemande.status)}</div>

                            <div className="maDetailsGrid">
                                <div className="maDetailCard">
                                    <div className="maMuted">Code mission</div>
                                    <div className="maStrong">{selectedDemande.ordreMissionCode}</div>
                                </div>
                                <div className="maDetailCard">
                                    <div className="maMuted">Date demande</div>
                                    <div className="maStrong">{formatDate(selectedDemande.createdAt)}</div>
                                </div>
                                <div className="maDetailCard">
                                    <div className="maMuted">Date début</div>
                                    <div className="maStrong">{formatDate(selectedDemande.ordreMissionDateDebut)}</div>
                                </div>
                                <div className="maDetailCard">
                                    <div className="maMuted">Date fin</div>
                                    <div className="maStrong">{formatDate(selectedDemande.ordreMissionDateFin)}</div>
                                </div>
                                {selectedDemande.ordreMissionLieu && (
                                    <div className="maDetailCard">
                                        <div className="maMuted">Lieu</div>
                                        <div className="maStrong">
                                            <MapPin size={14} style={{ display: "inline", marginRight: 4 }} />
                                            {selectedDemande.ordreMissionLieu}
                                        </div>
                                    </div>
                                )}
                                {selectedDemande.ordreMissionCentreCout && (
                                    <div className="maDetailCard">
                                        <div className="maMuted">Centre de coûts</div>
                                        <div className="maStrong">{selectedDemande.ordreMissionCentreCout}</div>
                                    </div>
                                )}
                            </div>

                            {selectedDemande.ordreMissionDescription && (
                                <div className="maBlock">
                                    <div className="maBlockTitle">Description de la mission</div>
                                    <div className="maBlockText">{selectedDemande.ordreMissionDescription}</div>
                                </div>
                            )}

                            <div className="maBlock">
                                <div className="maBlockTitle">Votre motif</div>
                                <div className="maBlockText">{selectedDemande.motif || "Aucun motif spécifié"}</div>
                            </div>

                            {selectedDemande.urgent && (
                                <div className="maBlock maBlock--soft">
                                    <div className="maBlockTitle">
                                        <AlertCircle size={18} style={{ color: "#ef4444" }} />
                                        Demande marquée comme urgente
                                    </div>
                                </div>
                            )}

                            {/* Validation manager */}
                            {selectedDemande.managerDecision !== null && (
                                <div className="maBlock">
                                    <div className="maBlockTitle">Validation manager</div>
                                    <div className="maDetailsLine">
                                        <span className="maMuted">Décision:</span>{" "}
                                        <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                            {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                        </b>
                                    </div>
                                    {selectedDemande.managerName && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">Par:</span> <b>{selectedDemande.managerName}</b>
                                        </div>
                                    )}
                                    {selectedDemande.managerDecisionAt && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">Date:</span> <b>{formatDate(selectedDemande.managerDecisionAt)}</b>
                                        </div>
                                    )}
                                    {selectedDemande.managerComment && (
                                        <div className="maReject" style={{ marginTop: 8 }}>
                                            <div className="maMuted">Commentaire</div>
                                            <div className="maRejectText">{selectedDemande.managerComment}</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Validation admin */}
                            {selectedDemande.adminDecision !== null && (
                                <div className="maBlock">
                                    <div className="maBlockTitle">Validation RH</div>
                                    <div className="maDetailsLine">
                                        <span className="maMuted">Décision:</span>{" "}
                                        <b style={{ color: selectedDemande.adminDecision ? "#10b981" : "#ef4444" }}>
                                            {selectedDemande.adminDecision ? "Approuvé" : "Rejeté"}
                                        </b>
                                    </div>
                                    {selectedDemande.adminName && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">Par:</span> <b>{selectedDemande.adminName}</b>
                                        </div>
                                    )}
                                    {selectedDemande.adminDecisionAt && (
                                        <div className="maDetailsLine">
                                            <span className="maMuted">Date:</span> <b>{formatDate(selectedDemande.adminDecisionAt)}</b>
                                        </div>
                                    )}
                                    {selectedDemande.adminComment && (
                                        <div className="maReject" style={{ marginTop: 8 }}>
                                            <div className="maMuted">Commentaire</div>
                                            <div className="maRejectText">{selectedDemande.adminComment}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="maModal__footer">
                            {(selectedDemande.status === "PENDING_MANAGER" || selectedDemande.status === "PENDING_ADMIN") && (
                                <button
                                    className="maBtn maBtn--danger"
                                    onClick={() => {
                                        setShowDetailsModal(false);
                                        handleCancel(selectedDemande.id);
                                    }}
                                >
                                    <X size={16} />
                                    Annuler la demande
                                </button>
                            )}
                            <button className="maBtn maBtn--ghost" onClick={() => setShowDetailsModal(false)}>
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MesOrdres;